import React, { useState } from 'react';
import { Button, Card, Descriptions, Space, Typography, Tag, Divider, Alert, Spin } from 'antd';
import { 
    CheckOutlined, 
    EditOutlined, 
    EnvironmentOutlined, 
    SunOutlined, 
    ThunderboltOutlined, 
    CarOutlined,
    FileTextOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const Step6Review = ({ onFinish, onBack, formData, isLastStep }) => {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        try {
            setLoading(true);
            await onFinish(formData);
        } catch (error) {
            console.error('Erro ao salvar:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderSiteInfo = () => (
        <Card 
            title={
                <Space>
                    <EnvironmentOutlined style={{ color: '#FB8500' }} />
                    <Text strong>Informações do Site</Text>
                </Space>
            }
            style={{ marginBottom: '16px' }}
        >
            <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Nome" span={2}>
                    <Text strong>{formData.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Endereço" span={2}>
                    {formData.logradouro}, {formData.numero}
                    {formData.complemento && `, ${formData.complemento}`}
                </Descriptions.Item>
                <Descriptions.Item label="Bairro">
                    {formData.bairro}
                </Descriptions.Item>
                <Descriptions.Item label="CEP">
                    {formData.cep}
                </Descriptions.Item>
                <Descriptions.Item label="Cidade/Estado" span={2}>
                    {formData.city} - {formData.state}
                </Descriptions.Item>
                {(formData.latitude || formData.longitude) && (
                    <Descriptions.Item label="Coordenadas" span={2}>
                        Lat: {formData.latitude || '-'} / Lng: {formData.longitude || '-'}
                    </Descriptions.Item>
                )}
                {formData.observacoes && (
                    <Descriptions.Item label="Observações" span={2}>
                        {formData.observacoes}
                    </Descriptions.Item>
                )}
            </Descriptions>
        </Card>
    );

    const renderSolarInfo = () => (
        <Card 
            title={
                <Space>
                    <SunOutlined style={{ color: '#FFB100' }} />
                    <Text strong>Usina Solar / Inversor</Text>
                    <Tag color="success">Obrigatório</Tag>
                </Space>
            }
            style={{ marginBottom: '16px' }}
        >
            {formData.hasGoodwe ? (
                <Alert
                    message="Vinculado à Planta Goodwe"
                    description={`ID da Planta: ${formData.goodwePlantId || 'Não especificado'}`}
                    type="success"
                    showIcon
                    icon={<SunOutlined />}
                />
            ) : (
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="Nome do Inversor" span={2}>
                        {formData.inversorNome || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Número de Série">
                        {formData.inversorSerial || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Capacidade (kWp)">
                        {formData.pvCapacity || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Fabricante">
                        {formData.inversorFabricante || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Modelo">
                        {formData.inversorModelo || '-'}
                    </Descriptions.Item>
                </Descriptions>
            )}
        </Card>
    );

    const renderBateriaInfo = () => (
        <Card 
            title={
                <Space>
                    <ThunderboltOutlined style={{ color: '#1890ff' }} />
                    <Text strong>Banco de Baterias</Text>
                    <Tag color={formData.hasBattery ? 'processing' : 'default'}>
                        {formData.hasBattery ? 'Sim' : 'Não possui'}
                    </Tag>
                </Space>
            }
            style={{ marginBottom: '16px' }}
        >
            {formData.hasBattery ? (
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="Capacidade (kWh)">
                        {formData.batteryCapacity || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Fabricante">
                        {formData.bateriaFabricante || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gerente Responsável" span={2}>
                        {formData.bateriaGerenteNome || '-'} ({formData.bateriaGerenteEmail || '-'})
                    </Descriptions.Item>
                    <Descriptions.Item label="Endereço" span={2}>
                        {formData.bateriaSameAddress ? (
                            <Tag color="blue">Mesmo endereço do site</Tag>
                        ) : (
                            `${formData.bateriaLogradouro}, ${formData.bateriaNumero} - ${formData.bateriaBairro}, ${formData.bateriaCidade}`
                        )}
                    </Descriptions.Item>
                </Descriptions>
            ) : (
                <Alert
                    message="Este site não possui banco de baterias"
                    type="info"
                    showIcon
                />
            )}
        </Card>
    );

    const renderGridInfo = () => (
        <Card 
            title={
                <Space>
                    <ThunderboltOutlined style={{ color: '#722ed1' }} />
                    <Text strong>Unidade Consumidora (Grid)</Text>
                    <Tag color="success">Obrigatório</Tag>
                </Space>
            }
            style={{ marginBottom: '16px' }}
        >
            <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Concessionária" span={2}>
                    {formData.gridConcessionaria || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Nº Instalação" span={2}>
                    {formData.gridNumeroInstalacao || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Conta de Luz (PDF)" span={2}>
                    {formData.gridPdfFile ? (
                        <Space>
                            <FileTextOutlined style={{ color: '#52c41a' }} />
                            <Text type="success">{formData.gridPdfFile.name}</Text>
                        </Space>
                    ) : (
                        <Text type="danger">Nenhum arquivo enviado</Text>
                    )}
                </Descriptions.Item>
            </Descriptions>
        </Card>
    );

    const renderCarregadoresInfo = () => (
        <Card 
            title={
                <Space>
                    <CarOutlined style={{ color: '#52c41a' }} />
                    <Text strong>Estações de Recarga (Carregadores EV)</Text>
                    <Tag color={formData.hasChargers ? 'success' : 'default'}>
                        {formData.hasChargers ? `${formData.carregadores?.length || 0} carregador(es)` : 'Não possui'}
                    </Tag>
                </Space>
            }
            style={{ marginBottom: '16px' }}
        >
            {formData.hasChargers && formData.carregadores?.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {formData.carregadores.map((charger, index) => (
                        <Card size="small" key={charger.id} style={{ backgroundColor: '#f6ffed' }}>
                            <Space split={<Divider type="vertical" />}>
                                <Text strong>Carregador {index + 1}</Text>
                                <Text>Serial: {charger.serial}</Text>
                                <Tag color="orange">{charger.potencia} kW</Tag>
                            </Space>
                        </Card>
                    ))}
                </Space>
            ) : (
                <Alert
                    message="Este site não possui carregadores de veículos elétricos"
                    type="info"
                    showIcon
                />
            )}
        </Card>
    );

    return (
        <div>
            <Alert
                message="Revise todas as informações antes de finalizar"
                description="Após confirmar, o site será criado com todos os equipamentos vinculados."
                type="warning"
                showIcon
                style={{ marginBottom: '24px' }}
            />

            {renderSiteInfo()}
            {renderSolarInfo()}
            {renderBateriaInfo()}
            {renderGridInfo()}
            {renderCarregadoresInfo()}

            <Card style={{ marginTop: '24px', backgroundColor: '#f0f5ff' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={5}>
                        <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                        Pronto para Finalizar
                    </Title>
                    <Text type="secondary">
                        Ao clicar em "Criar Site", o sistema irá:
                    </Text>
                    <ul style={{ marginLeft: '20px' }}>
                        <li>Cadastrar o site no banco de dados</li>
                        <li>Vincular os 4 tipos de equipamento (Solar, Bateria, Grid, Carregadores)</li>
                        {formData.hasGoodwe && <li>Sincronizar dados em tempo real com a API Goodwe</li>}
                        {formData.gridPdfFile && <li>Processar a conta de luz para extração de dados</li>}
                    </ul>
                    
                    <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <Button onClick={onBack} disabled={loading}>
                            <EditOutlined /> Voltar e Editar
                        </Button>
                        <Button 
                            type="primary"
                            size="large"
                            icon={<CheckOutlined />}
                            onClick={handleConfirm}
                            loading={loading}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            {loading ? 'Criando Site...' : 'Criar Site'}
                        </Button>
                    </Space>
                </Space>
            </Card>
        </div>
    );
};

export default Step6Review;
